import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Grid, Button, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Chip, Tooltip, Badge
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import SchoolIcon from "@mui/icons-material/School";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaletteIcon from "@mui/icons-material/Palette";
import AddIcon from "@mui/icons-material/Add";
import { fetchData, postData, putData, deleteData } from "../../../BaseUrl";
import Toast from "../../../DynamicComponent/Toast";

const GRADE_OPTIONS = [
  "Mun Mazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8",
];

const GRADE_LEVELS = {
  "Mun Mazhalai": "Early Learning Level",
  "Mazhalai": "Early Learning Level",
  "Nilai 1": "Foundation Level",
  "Nilai 2": "Foundation Level",
  "Nilai 3": "Intermediate Level",
  "Nilai 4": "Intermediate Level",
  "Nilai 5": "Intermediate Level",
  "Nilai 6": "Advanced Level",
  "Nilai 7": "Advanced Level",
  "Nilai 8": "Advanced Level",
};

const DEFAULT_COLORS = [
  "#244C77", "#4A7BA8", "#3B82F6", "#10B981",
  "#F59E0B", "#EF4444", "#336599", "#14B8A6",
  "#F97316", "#336599",
];

const FALLBACK_CARDS = GRADE_OPTIONS.map((grade, i) => ({
  _id: String(i + 1), grade, title: grade, subject: "Tamil", description: "",
  color: DEFAULT_COLORS[i % DEFAULT_COLORS.length], subjectCount: Math.floor(Math.random() * 5) + 1,
}));

const emptyForm = { grade: "", title: "", subject: "", description: "" };

function ConfirmDialog({ open, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, fontSize: "16px" }}>Confirm Delete</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "14px", color: "#374151" }}>Are you sure you want to delete this curriculum card?</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
        <Button onClick={onConfirm} variant="contained" color="error" sx={{ textTransform: "none", borderRadius: "8px" }}>Delete</Button>
      </DialogActions>
    </Dialog>
  );
}

function CurriculumConfig() {
  const [cards, setCards] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const load = useCallback(async () => {
    try {
      const res = await fetchData("admin/curriculum/list");
      setCards(Array.isArray(res?.data) ? res.data : FALLBACK_CARDS);
    } catch { setCards(FALLBACK_CARDS); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const showToast = (severity, message) => setToast({ open: true, severity, message });

  const openAdd = () => { setEditId(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (card) => {
    setEditId(card._id || card.id);
    setForm({ grade: card.grade || "", title: card.title || "", subject: card.subject || "", description: card.description || "" });
    setDialogOpen(true);
  };
  const closeDialog = () => { setDialogOpen(false); setForm(emptyForm); setEditId(null); };

  const handleColorChange = async (card, color) => {
    const id = card._id || card.id;
    setCards(prev => prev.map(c => (c._id === id || c.id === id) ? { ...c, color } : c));
    try {
      await putData(`admin/curriculum/update/${id}`, { color });
      showToast("success", "Card color updated.");
    } catch { showToast("error", "Failed to update color."); }
  };

  const handleSave = async () => {
    if (!form.grade) { showToast("error", "Grade is required."); return; }
    if (!form.title.trim()) { showToast("error", "Title is required."); return; }
    try {
      if (editId) {
        await putData(`admin/curriculum/update/${editId}`, form);
        showToast("success", "Curriculum updated.");
      } else {
        await postData("admin/curriculum/create", form);
        showToast("success", "Curriculum added.");
      }
      closeDialog(); load();
    } catch { showToast("error", "Failed to save."); }
  };

  const handleDelete = async () => {
    const id = deleteTarget?._id || deleteTarget?.id;
    setConfirmOpen(false);
    try {
      await deleteData(`admin/curriculum/${id}`);
      showToast("success", "Curriculum deleted.");
      load();
    } catch { showToast("error", "Failed to delete."); }
  };

  const btnStyle = {
    background: "linear-gradient(90deg, #4A7BA8, #244C77)",
    color: "#fff", fontSize: "13px", textTransform: "none",
    borderRadius: "10px", px: 2.5, py: 1,
    "&:hover": { background: "linear-gradient(90deg, #4A7BA8, #244C77)" },
  };

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={handleDelete} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Curriculum / Curriculum displayed in the website
      </p>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <SchoolIcon sx={{ color: "#244C77", fontSize: "28px" }} />
          <Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "22px", color: "#111827" }}>Curriculum Management</Typography>
            <Typography sx={{ fontSize: "13px", color: "#6B7280" }}>Manage curriculum cards for each grade level</Typography>
          </Box>
        </Box>
        <Button startIcon={<AddIcon />} onClick={openAdd} sx={btnStyle}>Add Curriculum</Button>
      </Box>

      <Grid container spacing={2.5}>
        {cards.map(card => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={card._id || card.id}>
            <Paper elevation={0} sx={{
              borderRadius: "16px", overflow: "hidden",
              boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
              transition: "transform 0.2s", "&:hover": { transform: "translateY(-3px)" },
            }}>
              {/* Colored header */}
              <Box sx={{
                backgroundColor: card.color || "#244C77", p: 2,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <Box>
                  <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "16px" }}>{card.grade}</Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.8)", fontSize: "12px" }}>
                    {GRADE_LEVELS[card.grade] || "Learning Level"}
                  </Typography>
                </Box>
                <Badge badgeContent={card.subjectCount || 0} color="default"
                  sx={{ "& .MuiBadge-badge": { backgroundColor: "rgba(255,255,255,0.9)", color: card.color || "#244C77", fontWeight: 700 } }}>
                  <SchoolIcon sx={{ color: "rgba(255,255,255,0.9)", fontSize: "28px" }} />
                </Badge>
              </Box>

              {/* Card body */}
              <Box sx={{ p: 2, backgroundColor: "#fff" }}>
                <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#111827", mb: 0.5 }}>
                  {card.title}
                </Typography>
                {card.subject && (
                  <Chip label={card.subject} size="small"
                    sx={{ backgroundColor: "#EEF2FF", color: "#244C77", fontWeight: 600, fontSize: "11px", mb: 1 }} />
                )}
                {card.description && (
                  <Typography sx={{ fontSize: "12px", color: "#6B7280", mb: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {card.description}
                  </Typography>
                )}

                {/* Actions row */}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1, pt: 1, borderTop: "1px solid #F3F4F6" }}>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEdit(card)} sx={{ color: "#244C77", "&:hover": { backgroundColor: "#EEF2FF" } }}>
                        <EditRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => { setDeleteTarget(card); setConfirmOpen(true); }} sx={{ color: "#EF4444", "&:hover": { backgroundColor: "#FEF2F2" } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View">
                      <IconButton size="small" sx={{ color: "#10B981", "&:hover": { backgroundColor: "#F0FDF4" } }}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Tooltip title="Change card color">
                    <IconButton size="small" component="label" sx={{ color: "#6B7280" }}>
                      <PaletteIcon fontSize="small" />
                      <input type="color" hidden value={card.color || "#244C77"}
                        onChange={e => handleColorChange(card, e.target.value)} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "17px", color: "#111827" }}>
          {editId ? "Edit Curriculum" : "Add Curriculum"}
        </DialogTitle>
        <DialogContent sx={{ pt: "12px !important" }}>
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Nilai / Grade</InputLabel>
            <Select value={form.grade} onChange={e => setForm(p => ({ ...p, grade: e.target.value }))} label="Nilai / Grade"
              sx={{ borderRadius: "10px" }}>
              {GRADE_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Title" fullWidth size="small" value={form.title}
            onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Subject" fullWidth size="small" value={form.subject}
            onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
          <TextField label="Description" fullWidth size="small" multiline rows={3} value={form.description}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none", color: "#6B7280" }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" sx={{ ...btnStyle, px: 3, py: 0.8 }}>
            {editId ? "Update" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CurriculumConfig;
